import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import {
  LancamentoImportPdfOpcoes,
  LancamentoImportPdfResponse,
  LancamentoImportPlanilhaResponse,
  LancamentoImportResultado
} from './lancamentos-import.models';

/** Resposta bruta do GET import-resultado/{requestId}. */
interface LancamentoImportResultadoApi {
  requestId?: string;
  status?: string;
  resultado?: {
    requestId?: string;
    status?: string;
    sucesso?: boolean;
    inseridos?: number;
    falhas?: number;
    erro?: string;
    itens?: Array<{
      sucesso?: boolean;
      erro?: string;
      codiEmp?: number;
      codiLote?: number;
      numeLan?: number;
      dataLan?: string;
      cdebLan?: number;
      ccreLan?: number;
      vlorLan?: number;
      chisLan?: string;
      codiUsu?: string;
    }>;
  };
}

export interface LancamentoImportOpcoes {
  codigoEmpresa?: number | null;
  usuario?: string | null;
}

@Injectable({ providedIn: 'root' })
export class LancamentosService {

  private readonly serviceApi = environment.apiUrl;

  constructor(private readonly http: HttpClient) {}

  importarPorPlanilha(
    planilha: File,
    opcoes?: LancamentoImportOpcoes
  ): Observable<LancamentoImportPlanilhaResponse> {
    const formData = new FormData();
    formData.append('planilha', planilha);

    let params = new HttpParams();
    if (opcoes?.codigoEmpresa != null && opcoes.codigoEmpresa > 0) {
      params = params.set('codigoEmpresa', String(opcoes.codigoEmpresa));
    }
    const usuario = String(opcoes?.usuario ?? '').trim();
    if (usuario) {
      params = params.set('usuario', usuario);
    }

    return this.http.post<LancamentoImportPlanilhaResponse>(
      `${this.serviceApi}/v1/lancamentos/import-planilha`,
      formData,
      { params }
    );
  }

  importarPorPdf(
    pdf: File,
    opcoes?: LancamentoImportPdfOpcoes
  ): Observable<LancamentoImportPdfResponse> {
    const formData = new FormData();
    formData.append('pdf', pdf, pdf.name);

    let params = new HttpParams();
    if (opcoes?.contaCredito != null && opcoes.contaCredito > 0) {
      params = params.set('contaCredito', String(opcoes.contaCredito));
    }
    if (opcoes?.codiHis != null && opcoes.codiHis > 0) {
      params = params.set('codiHis', String(opcoes.codiHis));
    }
    const usuario = String(opcoes?.usuario ?? '').trim();
    if (usuario) {
      params = params.set('usuario', usuario);
    }

    return this.http.post<LancamentoImportPdfResponse>(
      `${this.serviceApi}/v1/lancamentos/import-pdf`,
      formData,
      { params }
    );
  }

  obterImportResultado(requestId: string): Observable<LancamentoImportResultado> {
    return this.http
      .get<LancamentoImportResultadoApi>(
        `${this.serviceApi}/v1/lancamentos/import-resultado/${requestId}`
      )
      .pipe(map((api) => this.mapResultadoApi(api)));
  }

  private mapResultadoApi(api: LancamentoImportResultadoApi): LancamentoImportResultado {
    const resultado = api?.resultado;
    const itens = (resultado?.itens ?? []).map((item) => ({
      codigoEmpresa: item.codiEmp != null ? String(item.codiEmp) : undefined,
      codigo: item.codiLote != null ? `Lote ${item.codiLote}` : undefined,
      mensagem: item.erro,
      erro: item.erro,
      sucesso: item.sucesso
    }));

    const status = this.resolverStatusImportacao(api?.status, resultado);
    const somenteFalhas = itens.filter((i) => i.sucesso === false || !!i.erro);
    const listaExibicao = status === 'SUCCESS' ? [] : somenteFalhas;

    return {
      requestId: api?.requestId ?? resultado?.requestId,
      status,
      mensagem: resultado?.erro,
      error: resultado?.erro,
      totalInseridos: resultado?.inseridos,
      totalFalhas: resultado?.falhas,
      itens: listaExibicao,
      detalhes: listaExibicao,
      falhas: listaExibicao
    };
  }

  /** Garante SUCCESS quando a API/connector gravou ok mas o status veio vazio ou PENDING. */
  private resolverStatusImportacao(
    statusApi?: string,
    resultado?: LancamentoImportResultadoApi['resultado']
  ): LancamentoImportResultado['status'] {
    const bruto = String(statusApi ?? resultado?.status ?? '').toUpperCase();
    if (bruto === 'SUCCESS' || bruto === 'OK' || bruto === 'CONCLUIDO' || bruto === 'CONCLUÍDO') {
      return 'SUCCESS';
    }
    if (bruto === 'PARTIAL' || bruto === 'PARCIAL') return 'PARTIAL';
    if (bruto === 'ERROR' || bruto === 'ERRO' || bruto === 'FAILED' || bruto === 'FALHA') {
      return 'ERROR';
    }
    if (bruto === 'NOT_FOUND') return 'NOT_FOUND';
    if (!resultado) return 'PENDING';

    const inseridos = resultado.inseridos ?? 0;
    const falhas = resultado.falhas ?? 0;
    if (resultado.sucesso && falhas === 0 && inseridos > 0) return 'SUCCESS';
    if (inseridos > 0 && falhas > 0) return 'PARTIAL';
    if (resultado.sucesso === false || falhas > 0) return 'ERROR';

    return 'PENDING';
  }
}
